<template>
  <manual-page
    title="Gestión de Órdenes"
    description="Monitorea y gestiona todas las órdenes de alimento del sistema."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'ordersIndex' }"
    action-text="Ver órdenes"
  >
    <!-- Overview -->
    <h2>¿Qué son las Órdenes?</h2>
    <p>
      Las órdenes son los pedidos de alimento que se generan automáticamente
      según los planes activos. Cada orden representa una entrega de alimento
      para una mascota.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Una orden incluye:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Cliente y mascota destinataria</li>
        <li>Composición de la dieta (porciones por ingrediente)</li>
        <li>Fecha de entrega programada</li>
        <li>Dirección de entrega</li>
        <li>Monto a cobrar</li>
        <li>Estado actual</li>
      </ul>
    </div>

    <!-- Order Flow -->
    <h2>Ciclo de Vida de una Orden</h2>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Pendiente de Pago</h3>
          <p class="text-gray-600">
            La orden se genera automáticamente según el plan. El sistema intenta
            cobrar al cliente en la fecha programada.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Pagada</h3>
          <p class="text-gray-600">
            El cobro fue exitoso. La orden entra en cola de producción y la
            sucursal responsable puede comenzar a prepararla.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">En Preparación</h3>
          <p class="text-gray-600">
            La sucursal está elaborando el alimento. Se preparan las porciones
            según la receta de la dieta.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Lista para Entrega</h3>
          <p class="text-gray-600">
            El alimento está preparado y empacado. Esperando ser enviado
            o que el cliente lo recoja.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">5</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Entregada</h3>
          <p class="text-gray-600">
            Orden completada exitosamente. El cliente recibió su alimento.
          </p>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <h2>Filtrar Órdenes</h2>
    <p>Usa los filtros para encontrar órdenes específicas:</p>
    <ul>
      <li><strong>Por estado:</strong> Pendiente, Pagada, En preparación, Lista, Entregada, Fallida</li>
      <li><strong>Por sucursal:</strong> Ver solo órdenes de una franquicia</li>
      <li><strong>Por fecha:</strong> Rango de fechas de entrega</li>
      <li><strong>Por búsqueda:</strong> Nombre de cliente o mascota</li>
    </ul>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Filtra por "Pagadas" para ver las órdenes que están
        listas para entrar a producción.
      </p>
    </div>

    <!-- Production -->
    <h2>Programa de Producción</h2>
    <p>
      El programa de producción te ayuda a planificar la elaboración de alimentos:
    </p>
    <ul>
      <li>Ver órdenes agrupadas por fecha de entrega</li>
      <li>Agrupar por tipo de alimento para producción en lote</li>
      <li>Coordinar entre diferentes sucursales</li>
      <li>Exportar lista de producción</li>
    </ul>

    <!-- Failed Orders -->
    <h2>Órdenes Fallidas</h2>
    <p>
      Cuando un cobro falla repetidamente, la orden pasa a estado "Fallida".
      Motivos comunes:
    </p>
    <ul>
      <li>Tarjeta vencida o bloqueada</li>
      <li>Fondos insuficientes</li>
      <li>Problema con el procesador de pagos</li>
    </ul>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Acción requerida:</strong> Contacta al cliente para que actualice
        su método de pago. Una vez resuelto, el sistema reintentará el cobro.
      </p>
    </div>

    <!-- Order Detail -->
    <h2>Detalle de Orden</h2>
    <p>Al abrir una orden, verás:</p>
    <ul>
      <li>Información del cliente y mascota</li>
      <li>Composición detallada (ingredientes y cantidades)</li>
      <li>Dirección de entrega</li>
      <li>Historial de estados</li>
      <li>Información de pago</li>
      <li>Notas y observaciones</li>
    </ul>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>Si tienes dudas sobre las órdenes:</p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualOrders",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualPlans", label: "Planes" },
        { route: "manualClients", label: "Clientes" },
        { route: "manualFranchises", label: "Franquicias" },
        { route: "manualReports", label: "Reportes" },
      ],
    };
  },
};
</script>
